import { useAuth0 } from "@auth0/auth0-react"
import { createContext, useContext, useState, useEffect, useCallback } from "react"
import axios from "axios"
import { baseURL } from "constants/api"
import useLocalStorage from "hooks/useLocalStorage"
import i18n from "helpers/i18n"
import { postItem } from "helpers/api"
import { getInitialTranslations } from "helpers/translations"
import { getPendingMenu, clearPendingMenu } from "helpers/pendingMenu"

const getInitialLanguage = () => {
  const savedLang = localStorage.getItem("language")
  if (savedLang) return JSON.parse(savedLang)

  const browserLang = navigator.language.split("-")[0] // "ru-RU" → "ru"
  return browserLang || "en"
}
const StateContext = createContext({
  restaurant_id: null,
  isLoggingOut: false,
  setRestaurant_id: () => {},
  setIsLoggingOut: () => {},
  setLanguage: () => {},
})

export const ContextProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth0()
  const [restaurant_id, setRestaurant_id] = useState(() => {
    const stored = localStorage.getItem("restaurant_id");
    // Handle "null", "undefined", or empty string as null
    if (!stored || stored === "null" || stored === "undefined") {
      return null;
    }
    return stored;
  })
  // const [isAuthorized, setIsAuthorized] = useState(isAuthenticated)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [settings, setSettings] = useState([])
  const [restaurants, setRestaurants] = useState([])
  const [loading, setLoading] = useState(true)
  const [apiCalled, setApiCalled] = useState(false)
  const [isAdmin, setIsAdmin] = useState(localStorage.getItem("isAdmin") || false)
  const [language, setLanguage] = useLocalStorage("language", getInitialLanguage())

  const changeLanguage = useCallback((language) => {
    // setCurrentLanguage(language)
    i18n.changeLanguage(language)
    setLanguage(language)
  }, [])

  useEffect(() => {
    if (!language) {
      const userLanguages = navigator.languages
      if (userLanguages && userLanguages.length > 0) {
        setLanguage(userLanguages[0].slice(0, 2))
      }
    }
  }, [])

  useEffect(() => {
    if (restaurants instanceof Array) {
      const adminRestaurant = restaurants.find((restaurant) => restaurant?.id == restaurant_id)
      console.log("restaurants", restaurants)
      console.log("adminRestaurant", adminRestaurant)
      console.log("restaurant_id", restaurant_id)

      if (adminRestaurant?.is_admin == 1) {
        localStorage.setItem("isAdmin", true)
        setIsAdmin(true)
      }
    }
  }, [restaurant_id, restaurants])

  const getSettings = async (restId = restaurant_id) => {
    try {
      setLoading(true)
      // Check for null, undefined, or invalid string values
      if (!restId || restId === "null" || restId === "undefined") {
        console.warn("restaurant_id is null, undefined, or invalid:", restId)
        setLoading(false)
        return
      }

      console.log(`Fetching settings from ${baseURL}/settings/${restId}`)
      const response = await axios.get(`${baseURL}/settings/${restId}`)
      console.log("response.data:", response.data)

      setSettings(response.data)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      console.error('Error while executing "getSettings" request:', error)
      // Don't throw - allow app to continue working
    }
  }

  const getRestaurants = async () => {
    try {
      console.log(`get Restaurants from ${baseURL}/restaurants/`)
      const response = await axios.get(`${baseURL}/restaurants/`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })

      setRestaurants(response.data)
      console.log("response.data", response.data)
      console.log("Restaurants fetched successfully")
      setLoading(false)
    } catch (error) {
      setLoading(false)
      console.error('Error while executing "getRestaurants" request:', error)
      // Set empty array to prevent further errors
      setRestaurants([])
    }
  }

  const getUserRestaurant = async () => {
    if (isAuthenticated && restaurant_id === null && !apiCalled && !isLoggingOut) {
      setApiCalled(true) // Set early to prevent multiple calls
      try {
        const url = `${baseURL}/restaurants/user/${user.sub}`

        const response = await axios.get(url, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        })

        console.log("response.data_restaurants/user", response.data)

        if (response.status === 200 && response.data.length > 0) {
          _setRestaurant_id(response.data[0].id)
        } else {
          console.log("User does not have a restaurant yet. Creating a new one...")
          await createRestaurant()
        }
      } catch (error) {
        if (error.response?.status === 404) {
          console.log("User does not have a restaurant yet. Creating a new one...")
          await createRestaurant()
        } else {
          console.error('Error while executing "getUserRestaurant" request:', error)
          console.log('Make sure the server is running!')
          // Don't block the app - user can still see the interface
        }
      }
    }
  }

  const createRestaurant = async () => {
    try {
      const response = await axios.post(`${baseURL}/restaurants`, {
        userSub: user.sub,
        email: user.email,
      })
      console.log("Restaurant created successfully")
      const newRestaurantId = response.data.id
      _setRestaurant_id(newRestaurantId)

      // Если пользователь распознал меню на стартовой странице — импортируем блюда
      await importPendingMenu(newRestaurantId)
    } catch (error) {
      console.error("Error creating restaurant:", error)
      console.log('Make sure the server is running!')
      // Allow app to continue - user will see empty state
    }
  }

  // Импорт распознанного меню (фото с стартовой страницы) в новый restaurant
  const importPendingMenu = async (restId) => {
    const dishes = getPendingMenu()
    if (!dishes || dishes.length === 0) return

    try {
      for (const dish of dishes) {
        await postItem({
          restaurant_id: restId,
          title: dish.title || "",
          description: dish.description || "",
          price: dish.price || 0,
          // Вырезанный со фото меню фрагмент (data-URI) — Cloudinary зальёт его при createDish
          image: dish.image || "",
          toppings: [],
          extras: [],
          group_obj: null,
          translations: getInitialTranslations(),
          translations_descriptions: getInitialTranslations(),
        })
      }
      console.log(`Imported ${dishes.length} dishes from recognized menu`)
    } catch (error) {
      console.error("Error importing recognized menu dishes:", error)
    } finally {
      clearPendingMenu()
    }
  }

  useEffect(() => {
    console.log("ContextProvider_useEffect_restaurantId :>> ", restaurant_id)
    // Only fetch settings if restaurant_id is valid
    if (restaurant_id !== null && restaurant_id !== "null" && restaurant_id !== "undefined" && restaurant_id !== "") {
      getSettings(restaurant_id)
    }
  }, [restaurant_id])

  useEffect(() => {
    console.log("isLoggingOut", isLoggingOut)
    console.log("isAuthenticated", isAuthenticated)

    if (isAuthenticated && !isLoggingOut && user?.sub) {
      getUserRestaurant()
      // Only fetch restaurants if we don't have them yet
      if (restaurants.length === 0) {
        getRestaurants()
      }
    }
  }, [isAuthenticated, isLoggingOut, user?.sub])

  const _setRestaurant_id = (id) => {
    console.log("_setRestaurant_id id :>> ", id)
    if (id !== null) {
      setRestaurant_id(id)
      localStorage.setItem("restaurant_id", id)
    } else {
      setRestaurant_id(null)
      localStorage.removeItem("restaurant_id")
    }
  }

  return (
    <StateContext.Provider
      value={{
        settings,
        loading,
        setLoading,
        restaurant_id,
        isLoggingOut,
        setRestaurant_id: _setRestaurant_id,
        setIsLoggingOut,
        restaurants,
        isAdmin,
        setIsAdmin,
        language,
        setLanguage: changeLanguage,
        // Expose methods for lazy loading
        getSettings,
        getRestaurants,
      }}
    >
      {children}
    </StateContext.Provider>
  )
}

export const useStateContext = () => useContext(StateContext)
